import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Input } from "@/components/ui/input";
import { Building2, Check, X, Pencil, Minus } from "lucide-react";
import { EmployeeWithProfile } from "@/pages/OrgChartPage";
import { Department, CanvasItem, ConnectionSide, NODE_WIDTH, NODE_HEIGHT, EMPLOYEE_NODE_HEIGHT } from "./types";
import { cn } from "@/lib/utils";

interface ConnectionPointsProps {
  item: CanvasItem;
  isSelected: boolean;
  toolMode: string;
  connectingFromId: string | null;
  connectingFromSide: ConnectionSide | null;
  onStartConnection: (e: React.MouseEvent, id: string, side: ConnectionSide) => void;
  onCompleteConnection: (e: React.MouseEvent, id: string, side: ConnectionSide) => void;
}

function ConnectionPoints({ item, isSelected, toolMode, connectingFromId, connectingFromSide, onStartConnection, onCompleteConnection }: ConnectionPointsProps) {
  if (toolMode !== 'connect' && !connectingFromId && !isSelected) return null;

  const sides: Array<{ side: ConnectionSide; className: string }> = [
    { side: 'top', className: 'left-1/2 -top-2 -translate-x-1/2' },
    { side: 'right', className: '-right-2 top-1/2 -translate-y-1/2' },
    { side: 'bottom', className: 'left-1/2 -bottom-2 -translate-x-1/2' },
    { side: 'left', className: '-left-2 top-1/2 -translate-y-1/2' },
  ];

  return (
    <>
      {sides.map(({ side, className }) => (
        <button
          key={side}
          className={cn(
            "absolute w-4 h-4 rounded-full border-2 transition-all z-10",
            className,
            connectingFromId === item.id && connectingFromSide === side
              ? 'bg-primary border-primary scale-125 shadow-lg shadow-primary/30'
              : connectingFromId
                ? 'bg-green-500 border-green-600 hover:scale-125 cursor-crosshair shadow-md'
                : 'bg-background border-primary/60 hover:bg-primary hover:border-primary hover:scale-125 shadow-sm'
          )}
          onMouseDown={(e) => {
            if (connectingFromId) {
              onCompleteConnection(e, item.id, side);
            } else {
              onStartConnection(e, item.id, side);
            }
          }}
        />
      ))}
    </>
  );
}

// --- Department Card ---
interface DepartmentCardProps {
  item: CanvasItem;
  dept: Department;
  isSelected: boolean;
  isBeingDragged: boolean;
  isConnecting: boolean;
  isReadOnly: boolean;
  toolMode: string;
  connectingFromId: string | null;
  connectingFromSide: ConnectionSide | null;
  onMouseDown: (e: React.MouseEvent) => void;
  onClick: (e: React.MouseEvent) => void;
  onRemove: () => void;
  onStartConnection: (e: React.MouseEvent, id: string, side: ConnectionSide) => void;
  onCompleteConnection: (e: React.MouseEvent, id: string, side: ConnectionSide) => void;
}

export function DepartmentCard({
  item, dept, isSelected, isBeingDragged, isConnecting, isReadOnly, toolMode,
  connectingFromId, connectingFromSide, onMouseDown, onClick, onRemove,
  onStartConnection, onCompleteConnection
}: DepartmentCardProps) {
  return (
    <div
      className={cn(
        "absolute flex flex-col items-center justify-center transition-all group select-none rounded-2xl",
        "bg-gradient-to-br from-primary to-primary/80 text-primary-foreground",
        "shadow-lg shadow-primary/20",
        isBeingDragged && 'shadow-2xl z-50 scale-105 opacity-90 rotate-1',
        isSelected && !isBeingDragged && 'ring-2 ring-offset-2 ring-primary shadow-xl',
        !isBeingDragged && !isSelected && 'hover:shadow-xl hover:-translate-y-0.5',
        isConnecting && 'ring-2 ring-green-500/60'
      )}
      style={{
        left: item.x,
        top: item.y,
        width: NODE_WIDTH,
        height: NODE_HEIGHT,
        cursor: !isReadOnly && toolMode === 'move' ? (isBeingDragged ? 'grabbing' : 'grab') : 'default'
      }}
      onClick={onClick}
      onMouseDown={onMouseDown}
    >
      {!isReadOnly && (
        <ConnectionPoints
          item={item} isSelected={isSelected} toolMode={toolMode}
          connectingFromId={connectingFromId} connectingFromSide={connectingFromSide}
          onStartConnection={onStartConnection} onCompleteConnection={onCompleteConnection}
        />
      )}

      <div className="w-8 h-8 rounded-xl bg-primary-foreground/15 flex items-center justify-center mb-1">
        <Building2 className="h-4 w-4" />
      </div>
      <span className="font-semibold text-sm text-center px-3 line-clamp-2 leading-tight">{dept.name}</span>

      {!isReadOnly && (
        <button
          onClick={(e) => { e.stopPropagation(); onRemove(); }}
          className="absolute -top-2 -right-2 p-1 rounded-full bg-destructive/90 shadow-lg hover:bg-destructive opacity-0 group-hover:opacity-100 transition-all z-20 hover:scale-110"
        >
          <Minus className="h-3 w-3 text-destructive-foreground" />
        </button>
      )}
    </div>
  );
}

// --- Employee Card ---
interface EmployeeCardProps {
  item: CanvasItem;
  emp: EmployeeWithProfile;
  isSelected: boolean;
  isBeingDragged: boolean;
  isConnecting: boolean;
  isReadOnly: boolean;
  toolMode: string;
  connectingFromId: string | null;
  connectingFromSide: ConnectionSide | null;
  editingEmployeeId: string | null;
  editPosition: string;
  editingTasksId: string | null;
  editTasksText: string;
  onMouseDown: (e: React.MouseEvent) => void;
  onClick: (e: React.MouseEvent) => void;
  onDoubleClick: (e: React.MouseEvent) => void;
  onRemove: () => void;
  onStartEditPosition: (e: React.MouseEvent) => void;
  onSavePosition: (e: React.MouseEvent) => void;
  onCancelEdit: (e: React.MouseEvent) => void;
  onEditPositionChange: (val: string) => void;
  onStartEditTasks: (e: React.MouseEvent) => void;
  onSaveTasks: (e: React.MouseEvent) => void;
  onCancelEditTasks: (e: React.MouseEvent) => void;
  onEditTasksChange: (val: string) => void;
  onStartConnection: (e: React.MouseEvent, id: string, side: ConnectionSide) => void;
  onCompleteConnection: (e: React.MouseEvent, id: string, side: ConnectionSide) => void;
}

export function EmployeeCard({
  item, emp, isSelected, isBeingDragged, isConnecting, isReadOnly, toolMode,
  connectingFromId, connectingFromSide,
  editingEmployeeId, editPosition, editingTasksId, editTasksText,
  onMouseDown, onClick, onDoubleClick, onRemove,
  onStartEditPosition, onSavePosition, onCancelEdit, onEditPositionChange,
  onStartEditTasks, onSaveTasks, onCancelEditTasks, onEditTasksChange,
  onStartConnection, onCompleteConnection,
}: EmployeeCardProps) {
  const isEditing = editingEmployeeId === emp.id;
  const isEditingTasks = editingTasksId === item.id;

  return (
    <div
      className={cn(
        "absolute flex flex-col transition-all group select-none rounded-2xl overflow-hidden",
        "bg-card border border-border/60 shadow-md",
        isBeingDragged && 'shadow-2xl z-50 scale-105 opacity-90 rotate-1',
        isSelected && !isBeingDragged && 'ring-2 ring-offset-2 ring-primary shadow-xl',
        !isBeingDragged && !isSelected && 'hover:shadow-xl hover:-translate-y-0.5',
        isConnecting && 'ring-2 ring-green-500/60'
      )}
      style={{
        left: item.x,
        top: item.y,
        width: NODE_WIDTH,
        minHeight: EMPLOYEE_NODE_HEIGHT,
        cursor: !isReadOnly && toolMode === 'move' && !isEditing && !isEditingTasks ? (isBeingDragged ? 'grabbing' : 'grab') : 'default'
      }}
      onClick={(e) => !isEditing && !isEditingTasks && onClick(e)}
      onMouseDown={(e) => !isEditing && !isEditingTasks && onMouseDown(e)}
      onDoubleClick={onDoubleClick}
    >
      {!isReadOnly && (
        <ConnectionPoints
          item={item} isSelected={isSelected} toolMode={toolMode}
          connectingFromId={connectingFromId} connectingFromSide={connectingFromSide}
          onStartConnection={onStartConnection} onCompleteConnection={onCompleteConnection}
        />
      )}

      {/* Header gradient */}
      <div className="h-1.5 bg-gradient-to-r from-primary via-primary/70 to-accent w-full" />

      <div className="p-3 flex-1 flex flex-col gap-2">
        {/* Employee info */}
        <div className="flex items-center gap-2.5">
          <Avatar className="h-10 w-10 ring-2 ring-primary/15 flex-shrink-0 shadow-sm">
            <AvatarImage src={emp.profile_image_url || undefined} />
            <AvatarFallback className="text-xs bg-primary/10 text-primary font-semibold">
              {emp.name.split(' ').map(n => n[0]).join('').slice(0, 2)}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <p className="text-xs font-semibold truncate text-foreground">{emp.name}</p>
            {isEditing ? (
              <div className="flex items-center gap-1 mt-0.5" onClick={(e) => e.stopPropagation()}>
                <Input
                  value={editPosition}
                  onChange={(e) => onEditPositionChange(e.target.value)}
                  className="h-5 text-[10px] px-1.5 border-primary/30 rounded-md"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') onSavePosition(e as unknown as React.MouseEvent);
                    if (e.key === 'Escape') onCancelEdit(e as unknown as React.MouseEvent);
                  }}
                  autoFocus
                />
                <button onClick={onSavePosition} className="p-0.5 hover:bg-primary/10 rounded"><Check className="h-3 w-3 text-primary" /></button>
                <button onClick={onCancelEdit} className="p-0.5 hover:bg-destructive/10 rounded"><X className="h-3 w-3 text-muted-foreground" /></button>
              </div>
            ) : (
              <div className="flex items-center gap-1">
                <p className="text-[10px] text-muted-foreground truncate flex-1">{emp.position}</p>
                {!isReadOnly && (
                  <button onClick={onStartEditPosition} className="p-0.5 hover:bg-muted rounded opacity-0 group-hover:opacity-100">
                    <Pencil className="h-2.5 w-2.5 text-muted-foreground" />
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Tasks */}
        <div className="border-t border-border/40 pt-1.5">
          {!isReadOnly && isEditingTasks ? (
            <div className="flex flex-col gap-1" onClick={(e) => e.stopPropagation()}>
              <textarea
                value={editTasksText}
                onChange={(e) => onEditTasksChange(e.target.value)}
                placeholder="Aufgaben..."
                className="text-[10px] bg-muted/50 border border-border/50 rounded-lg px-2 py-1 w-full focus:outline-none focus:ring-1 focus:ring-primary/40 resize-none text-foreground"
                rows={2}
                onKeyDown={(e) => { if (e.key === 'Escape') onCancelEditTasks(e as unknown as React.MouseEvent); }}
                autoFocus
              />
              <div className="flex justify-end gap-1">
                <button onClick={onSaveTasks} className="p-0.5 hover:bg-primary/10 rounded"><Check className="h-3 w-3 text-primary" /></button>
                <button onClick={onCancelEditTasks} className="p-0.5 hover:bg-muted rounded"><X className="h-3 w-3 text-muted-foreground" /></button>
              </div>
            </div>
          ) : isReadOnly ? (
            <p className="text-[10px] text-muted-foreground flex-1 line-clamp-2 min-h-[16px]">
              {item.tasks || <span className="italic opacity-50">Keine Aufgaben</span>}
            </p>
          ) : (
            <div
              className="flex items-start gap-1 cursor-pointer hover:bg-muted/50 rounded-lg p-1 -mx-1"
              onClick={onStartEditTasks}
            >
              <p className="text-[10px] text-muted-foreground flex-1 line-clamp-2 min-h-[16px]">
                {item.tasks || <span className="italic opacity-40">Aufgaben hinzufügen...</span>}
              </p>
              <Pencil className="h-2.5 w-2.5 opacity-0 group-hover:opacity-40 flex-shrink-0 mt-0.5 text-muted-foreground" />
            </div>
          )}
        </div>
      </div>

      {/* Remove button */}
      {!isReadOnly && (
        <button
          onClick={(e) => { e.stopPropagation(); onRemove(); }}
          className="absolute -top-2 -right-2 p-1 rounded-full bg-destructive/90 shadow-lg hover:bg-destructive opacity-0 group-hover:opacity-100 transition-all z-20 hover:scale-110"
        >
          <Minus className="h-3 w-3 text-destructive-foreground" />
        </button>
      )}
    </div>
  );
}
